import { debounce, Dialog, DialogContent, DialogTitle, InputAdornment, Stack, Typography } from '@mui/material'
import { C1IconButton } from 'c1ui'
import { C1Icon } from 'components/core/icons/C1Icon'
import { TextField } from 'components/core/textfield/index'
import { SpotlightStyles } from 'components/pages/spotlight/styles'
import { Search, X } from 'lucide-react'
import { AccessReviewSearchServiceSearchRequest } from 'pbts/c1/api/accessreview/v1/accessreview_search'
import { SearchAppsExpandedRequest } from 'pbts/c1/api/app/v1/app'
import { AppEntitlementSearchServiceSearchRequest as SearchEntitlementsRequest } from 'pbts/c1/api/app/v1/app_entitlement'
import { SearchUsersRequest } from 'pbts/c1/api/user/v1/user'
import React, { FC, useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { searchAccessReviewApi } from 'redux_toolkit/access-reviews'
import { appSearchApi } from 'redux_toolkit/apps'
import { appEntitlementsSearchApi } from 'redux_toolkit/entitlements'
import { userSearchApi } from 'redux_toolkit/users'
import { ListSearchResults } from './ListSearchResults'

type SpotlightProps = {
    open: boolean
    onClose: () => void
}

export const Spotlight: FC<SpotlightProps> = ({ open, onClose }) => {
    const [isEmptyState, setIsEmptyState] = React.useState(true)
    const [loadingError, setLoadingError] = React.useState('')
    const pageSize = 10
    const intl = useIntl()

    const {
        trigger: triggerSearchUsers,
        data: users,
        isLoading: isSearchingForUsers,
        isError: isSearchUsersError,
    } = userSearchApi.useLazySearch()

    const {
        trigger: triggerSearchEntitlements,
        data: entitlements,
        isLoading: isSearchingForEntitlements,
        isError: isSearchEntitlementsError,
    } = appEntitlementsSearchApi.useLazySearch()

    const {
        trigger: triggerSearchApps,
        data: apps,
        isLoading: isSearchingForApps,
        isError: isSearchAppsError,
    } = appSearchApi.useLazySearchAppsExpanded()

    const {
        trigger: triggerSearchAccessReviews,
        data: accessReviews,
        isLoading: isSearchingForAccessReviews,
        isError: isSearchAccessReviewsError,
    } = searchAccessReviewApi.useLazySearch()

    const handleClose = (): void => {
        setIsEmptyState(true)
        onClose()
    }

    const onFilterChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const searchText = event.target.value
            if (searchText === '') {
                setIsEmptyState(true)
                return
            }
            setIsEmptyState(false)
            triggerSearchUsers(
                SearchUsersRequest.create({
                    query: searchText,
                    expandMask: { paths: ['*'] },
                }),
                {
                    pageSize,
                    notify: true,
                    actionable: !!searchText,
                },
            )
            triggerSearchEntitlements(
                SearchEntitlementsRequest.create({
                    query: searchText,
                    expandMask: { paths: ['*'] },
                }),
                {
                    pageSize,
                    notify: true,
                    actionable: !!searchText,
                },
            )
            triggerSearchAccessReviews(
                AccessReviewSearchServiceSearchRequest.create({
                    query: searchText,
                    expandMask: { paths: ['*'] },
                }),
                {
                    pageSize,
                    notify: true,
                    actionable: !!searchText,
                },
            )
            triggerSearchApps(
                SearchAppsExpandedRequest.create({
                    query: searchText,
                    expandMask: { paths: ['*'] },
                }),
                {
                    pageSize,
                    notify: true,
                    actionable: !!searchText,
                },
            )
        },
        [triggerSearchUsers, triggerSearchEntitlements, triggerSearchAccessReviews, triggerSearchApps, pageSize],
    )

    const onQueryChangeDebounced = React.useMemo(() => debounce((searchText) => {
        onFilterChange(searchText)
    }, 400), [onFilterChange])

    React.useEffect(() => {
        if (isSearchingForUsers || isSearchingForEntitlements || isSearchingForAccessReviews || isSearchingForApps) {
            return
        }
        const erroredCalls: string[] = []
        if (isSearchUsersError) {
            erroredCalls.push(intl.formatMessage({ id: 'spotlight_users', defaultMessage: 'Users' }))
        }
        if (isSearchEntitlementsError) {
            erroredCalls.push(intl.formatMessage({ id: 'spotlight_entitlements', defaultMessage: 'Entitlements' }))
        }
        if (isSearchAccessReviewsError) {
            erroredCalls.push(intl.formatMessage({ id: 'spotlight_access_reviews', defaultMessage: 'Access reviews' }))
        }
        if (isSearchAppsError) {
            erroredCalls.push(intl.formatMessage({ id: 'spotlight_apps', defaultMessage: 'Applications' }))
        }
        const errorCount = erroredCalls.length
        if (errorCount === 4) {
            setLoadingError(intl.formatMessage({ id: 'search_error_all', defaultMessage: 'Failed to fetch results. Please try again later.' }))
        } else if (errorCount) {
            const concatenatedObjects = erroredCalls.join(', ').replace(/,([^,]*)$/, ' and$1')
            setLoadingError(intl.formatMessage({ id: 'search_error', defaultMessage: 'Failed to fetch results for {objects}' }, {
                objects: concatenatedObjects,
            }))
        } else {
            // No error, clear the error message
            setLoadingError('')
        }
    }, [intl, isSearchUsersError, isSearchEntitlementsError, isSearchAccessReviewsError, isSearchAppsError,
        isSearchingForUsers, isSearchingForEntitlements, isSearchingForAccessReviews, isSearchingForApps])

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            sx={SpotlightStyles.dialog}
        >
            <DialogTitle>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <Typography variant="h5" component="h2">
                        <FormattedMessage id="spotlight_search_title" defaultMessage="Search" />
                    </Typography>
                    <C1IconButton onClick={handleClose} aria-label={intl.formatMessage({ id: 'spotlight_close', defaultMessage: 'Close search' })}><C1Icon icon={X} size="sm" /></C1IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Stack pt={1}>
                    <TextField
                        onChange={onQueryChangeDebounced}
                        placeholder={intl.formatMessage({
                            id: 'spotlight_search_placeholder',
                            defaultMessage: 'Search for users, apps, entitlements or campaigns',
                        })}
                        inputProps={{ 'aria-label': 'search' }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <C1Icon icon={Search} size="sm" sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                        }}
                        autoComplete="off"
                        autoFocus
                    />
                    <ListSearchResults
                        entitlements={entitlements[0]}
                        users={users[0]}
                        campaigns={accessReviews[0]}
                        apps={apps[0]}
                        isLoading={isSearchingForUsers || isSearchingForEntitlements || isSearchingForAccessReviews || isSearchingForApps}
                        isEmpty={isEmptyState}
                        handleClose={handleClose}
                        loadingErrorText={loadingError}
                    />
                </Stack>
            </DialogContent>
        </Dialog>
    )
}
